import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { toUserRole } from "../utils/RoleUtils";

export interface User {
  _id: string;
  accountId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  avatar?: string;
  emailVerified: boolean;
  termsAccepted: boolean;
  organizations: UserOrganization[];
  refreshToken: string | null;
  createdAt?: Date;
  updatedAt?: Date;
  lastLogin?: Date;
}

export interface UserOrganization {
  organizationId: string;
  role: UserRole;
  pending: boolean;
  status: MemberStatus;
}

export enum UserRole {
  SuperAdmin = "superadmin",
  Admin = "admin",
  Owner = "owner",
  Member = "member",
}

export enum MemberStatus {
  Active = "active",
  Inactive = "inactive",
  Pending = "pending",
}

export enum InvitationStatus {
  Pending = "pending",
  Accepted = "accepted",
  Revoked = "revoked",
  Declined = "declined",
  Expired = "expired",
}

export interface Invitation {
  _id: string;
  email: string;
  organizationId: string;
  role: UserRole;
  status: InvitationStatus;
  token: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterUserResponse {
  user: {
    id: string;
    accountId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    isAdmin: boolean;
    emailVerified: boolean;
    avatar?: string;
    termsAccepted: boolean;
    organizations: {
      organizationId: string;
      role: UserRole;
      pending: boolean;
      status: string;
    }[];
    createdAt?: string;
    updatedAt?: string;
    lastLogin?: string;
  };
  token: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
}

export interface DataExportResponse {
  message: string;
  email: string;
  jobId?: string;
  expirationDays?: number;
}

export interface AccountDeletionRequest {
  reason?: string;
  additionalInfo?: string;
}

export interface AccountDeletionResponse {
  message: string;
}

export interface FullAccountDeletionRequest {
  password: string;
  confirmation: "DELETE";
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  status: number;
}

export interface Organization {
  organizationId: string;
  name: string;
  role: UserRole;
}

export interface UserDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizations: Organization[];
  lastLogin: string;
}

export interface LoginUserResponse {
  accessToken: string;
  refreshToken: string;
  user: UserDetails;
  message: string;
}

export interface OrganizationMember {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  avatar: string | null;
  role: UserRole;
  pending: boolean;
  status: string;
  organizationName: string;
  invitationDate: string | null;
  expiresAt: string | null;
}

export interface AssociatedOrganization {
  id: string;
  name: string;
  role: UserRole;
  pending: boolean;
  status: string;
}

export interface CurrentOrganizationDetails {
  id: string;
  organizationId: string;
  name: string;
  owner: string;
  memberCount: number;
}

export interface OrganizationOverview {
  currentOrganization: CurrentOrganizationDetails;
  associatedOrganizations: AssociatedOrganization[];
}

export class UserService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/users";
  }

  async uploadAvatar(file: File, config?: AxiosRequestConfig) {
    const formData = new FormData();
    formData.append("avatar", file);

    const response = await httpService.post<ApiResponse<{ avatar: string }>>(
      `${this.baseUrl}/profile/upload-avatar`,
      formData,
      {
        ...config,
        headers: { "Content-Type": "multipart/form-data", ...config?.headers },
      }
    );

    return response.data.avatar;
  }

  async getOrganizationOverview(
    config?: AxiosRequestConfig
  ): Promise<{ data: OrganizationOverview; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<OrganizationOverview>>(
        `${this.baseUrl}/organizations/overview`,
        config
      );

      // Convert role strings to UserRole enum for type safety
      const data: OrganizationOverview = {
        ...response.data,
        associatedOrganizations: response.data.associatedOrganizations.map(
          (org) => ({
            ...org,
            role: toUserRole(org.role as unknown as string),
          })
        ),
      };

      return {
        data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error fetching organization overview:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch organization overview.";
      throw new Error(errorMessage);
    }
  }

  async loginUser(
    user: Pick<User, "email" | "password">,
    config?: AxiosRequestConfig
  ): Promise<{ data: LoginUserResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<LoginUserResponse>,
        Pick<User, "email" | "password">
      >(`${this.baseUrl}/auth/login`, user, config);

      // Convert role strings to UserRole enum
      const data: LoginUserResponse = {
        ...response.data,
        user: {
          ...response.data.user,
          organizations: response.data.user.organizations.map((org) => ({
            ...org,
            role: toUserRole(org.role as unknown as string),
          })),
        },
      };

      return {
        data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "An unexpected error occurred.";
      throw new Error(errorMessage);
    }
  }

  async logoutUser(): Promise<void> {
    try {
      await httpService.post("/users/auth/logout", {});
    } catch (error) {
      console.error("Error logging out:", error);
    }
  }

  async registerUser(
    user: {
      firstName: string;
      lastName: string;
      password: string;
      email: string;
    },
    config?: AxiosRequestConfig
  ): Promise<{ data: RegisterUserResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<RegisterUserResponse>
      >(`${this.baseUrl}/auth/register`, user, config);

      // Convert role strings to UserRole enum
      const data: RegisterUserResponse = {
        ...response.data,
        user: {
          ...response.data.user,
          organizations: response.data.user.organizations.map((org) => ({
            ...org,
            role: toUserRole(org.role as unknown as string),
          })),
        },
      };

      return { data, message: response.message };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "An unexpected error occurred during registration.";
      throw new Error(errorMessage);
    }
  }

  async verifyEmail(token: string): Promise<{ data: {}; message: string }> {
    console.log("Starting verifyEmail function, token:", token);
    try {
      const response = await httpService.get<ApiResponse<{}>>(
        `${this.baseUrl}/auth/verify-email/${token}`
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      console.error("Error in verifyEmail request:", error);

      const errorMessage =
        error?.response?.data?.message ||
        "An error occurred during verification.";
      throw new Error(errorMessage);
    }
  }

  async inviteUsersToOrganization(
    emails: string[],
    role: UserRole = UserRole.Member,
    config?: AxiosRequestConfig
  ): Promise<{
    results: { email: string; status: string; message: string }[];
  }> {
    if (!emails || emails.length === 0) {
      throw new Error("Emails array must contain at least one email.");
    }

    try {
      const response = await httpService.post<
        ApiResponse<{
          results: { email: string; status: string; message: string }[];
        }>
      >(`${this.baseUrl}/organizations/invite`, { emails, role }, config);

      return response.data;
    } catch (error: any) {
      console.error("Failed to invite users:", error);
      const errorMessage =
        error?.response?.data?.message || "An unexpected error occurred.";
      throw new Error(errorMessage);
    }
  }

  async acceptInvitation(
    token: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    try {
      const response = await httpService.post<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/invite/accept`,
        { token },
        config
      );
      return { message: response.message };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "An error occurred while accepting the invitation.";
      throw new Error(errorMessage);
    }
  }

  async declineInvitation(
    token: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    try {
      const response = await httpService.post<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/invite/decline`,
        { token },
        config
      );
      return { message: response.message };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "An error occurred while declining the invitation.";
      throw new Error(errorMessage);
    }
  }

  async removeMember(
    userId: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: any; message: string }> {
    if (!userId) {
      throw new Error("User ID is required to remove a member.");
    }

    try {
      const response = await httpService.delete<ApiResponse<any>>(
        `${this.baseUrl}/organizations/members/remove`,
        {
          ...config,
          data: { userId },
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to remove user.";
      throw new Error(errorMessage);
    }
  }

  async activateMember(
    userId: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    if (!userId) {
      throw new Error("User ID is required to transfer ownership.");
    }

    try {
      const response = await httpService.patch<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/activate-member`,
        { userId },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to activate member.";
      throw new Error(errorMessage);
    }
  }

  async leaveOrganization(
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    try {
      const response = await httpService.patch<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/leave-organization`,
        {},
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update user role.";
      throw new Error(errorMessage);
    }
  }

  async resendInvitation(
    email: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    if (!email) {
      throw new Error("Email is required to resend an invitation.");
    }

    try {
      const response = await httpService.post<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/invite/resend-invitation`,
        { email },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to resend invitation.";
      throw new Error(errorMessage);
    }
  }

  async updateUserRole(
    userId: string,
    role: UserRole,
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    if (!userId || !role) {
      throw new Error("User ID and Role are required to update a user's role.");
    }

    try {
      const response = await httpService.patch<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/members/role`,
        { userId, role },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update user role.";
      throw new Error(errorMessage);
    }
  }

  async transferOwnership(
    newOwnerId: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    if (!newOwnerId) {
      throw new Error("User ID is required to transfer ownership.");
    }

    try {
      const response = await httpService.patch<ApiResponse<{}>>(
        `${this.baseUrl}/organizations/transfer-ownership`,
        { newOwnerId },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to transfer ownership.";
      throw new Error(errorMessage);
    }
  }

  async requestPasswordReset(
    email: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    try {
      const response = await httpService.post<ApiResponse<{}>>(
        `${this.baseUrl}/auth/password/request`,
        { email },
        config
      );
      return { message: response.message };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "An unexpected error occurred while sending the password reset email.";
      throw new Error(errorMessage);
    }
  }

  async passwordReset(
    token: string,
    newPassword: string,
    config?: AxiosRequestConfig
  ): Promise<{ message: string }> {
    try {
      const response = await httpService.post<ApiResponse<{}>>(
        `${this.baseUrl}/auth/password/reset`,
        { token, newPassword },
        config
      );
      return { message: response.message };
    } catch (error: any) {
      console.error("Failed to reset password:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "An unexpected error occurred while resetting the password.";
      throw new Error(errorMessage);
    }
  }

  async updateUserProfile(
    data: Partial<User>,
    config?: AxiosRequestConfig
  ): Promise<User> {
    try {
      const response = await httpService.patch<{ data: { user: User } }>(
        `${this.baseUrl}/profile`,
        data,
        config
      );

      // Convert role strings to UserRole enum
      const user: User = {
        ...response.data.user,
        organizations: response.data.user.organizations.map((org) => ({
          ...org,
          role: toUserRole(org.role as unknown as string),
        })),
      };

      return user;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async getCurrentUser(config?: AxiosRequestConfig): Promise<User> {
    try {
      const response = await httpService.get<ApiResponse<{ user: User }>>(
        `${this.baseUrl}/profile`,
        config
      );

      // Convert role strings to UserRole enum
      const user: User = {
        ...response.data.user,
        organizations: response.data.user.organizations.map((org) => ({
          ...org,
          role: toUserRole(org.role as unknown as string),
        })),
      };

      return user;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async checkSession(
    config?: AxiosRequestConfig
  ): Promise<{ authenticated: boolean }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ authenticated: boolean }>
      >(`${this.baseUrl}/auth/check-session`, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async resetPassword(
    email: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      const response = await httpService.post(
        `${this.baseUrl}/password/request`,
        { email },
        config
      );
      console.log("Password reset email sent:", response);
    } catch (error) {
      console.error("Failed to send password reset email:", error);
      throw error;
    }
  }

  async getOrganizationMembers(
    config?: AxiosRequestConfig
  ): Promise<OrganizationMember[]> {
    try {
      const response = await httpService.get<
        ApiResponse<{ members: OrganizationMember[] }>
      >(`${this.baseUrl}/organizations/members`, config);
      // Convert role strings to UserRole enum for type safety
      return response.data.members.map((member) => ({
        ...member,
        role: toUserRole(member.role as unknown as string),
      }));
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async refreshToken(
    config?: AxiosRequestConfig
  ): Promise<RefreshTokenResponse> {
    try {
      return await httpService.post<RefreshTokenResponse>(
        "/tokens/refresh",
        {},
        config
      );
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async requestDataExport(
    config?: AxiosRequestConfig
  ): Promise<{ data: DataExportResponse; message: string }> {
    try {
      const response = await httpService.post<ApiResponse<DataExportResponse>>(
        "/users/me/export/request",
        {},
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to request data export. Please try again.";
      throw new Error(errorMessage);
    }
  }

  async requestAccountDeletion(
    data: AccountDeletionRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: AccountDeletionResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<AccountDeletionResponse>
      >("/users/account/request-deletion", data, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to submit deletion request. Please try again.";
      throw new Error(errorMessage);
    }
  }

  async deleteUserAccount(
    data: FullAccountDeletionRequest,
    config?: AxiosRequestConfig
  ): Promise<{ data: AccountDeletionResponse; message: string }> {
    try {
      const response = await httpService.delete<
        ApiResponse<AccountDeletionResponse>
      >("/users/account/delete", {
        ...config,
        data: data,
      });
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to delete account. Please try again.";
      throw new Error(errorMessage);
    }
  }

  private handleError(error: any): void {
    console.error("API call failed:", error);
  }
}

export const userService = new UserService();
